import {useState, useEffect} from 'react';
import ReactSelect from 'react-select';
import Modal from '../../../components/Modal/Modal';
import {PlantingProjectType} from '../../../entities';
import {
    CheckboxLabel,
    DateControl,
    FormFieldContainer,
    Label,
    LabelContainer,
    NotValidToSaveValidation,
    NotValidToShowToPartnerValidation,
    NumberControl,
    RequiredSignifier,
    RequiredToShowToPartnerSignifier,
    TextControl
} from '../../../components/Form/FormStyles';
import {CancelButton, SaveChangesButton} from '../../../components/Buttons/SaveChanges';
import {ValidateControl} from '../../../components/Controls/ValidateControl';
import styled from 'styled-components';

type PartnerOption = {Id: number; Name: string};

type ParticipationData = {
    Id: number;
    PartnerId: number;
    PlantingProjectId: number;
    FundedYear: number | null;
    FundedYearValidated: boolean;
    FundingSource: string | null;
    DollarsDonated: number | null;
    DollarsDonatedValidated: boolean;
    TTFTrees: number | null;
    TTFTreesValidated: boolean;
    CommunityTreesPlanted: number | null;
    CommunityTreesPlantedValidated: boolean;
    CommunityTreesDistributed: number | null;
    CommunityTreesDistributedValidated: boolean;
    CommunityBudgetPassThrough: number | null;
    CommunityBudgetPassThroughValidated: boolean;
    Excluded: boolean;
    ShowOnDate: string | null;
    CarbonPurchaseAmount: number | null;
    CarbonCreditsTransacted: number | null;
    VintageYear: string | null;
    SerialNumber: string | null;
    ParticipantRegistryLink: string | null;
};

type FormData = {
    partnerId: number | null;
    fundedYear: string;
    fundedYearValidated: boolean;
    fundingSource: string;
    dollarsDonated: string;
    dollarsDonatedValidated: boolean;
    ttfTrees: string;
    ttfTreesValidated: boolean;
    communityTreesPlanted: string;
    communityTreesPlantedValidated: boolean;
    communityTreesDistributed: string;
    communityTreesDistributedValidated: boolean;
    communityBudgetPassThrough: string;
    communityBudgetPassThroughValidated: boolean;
    excluded: boolean;
    showOnDate: string;
    carbonPurchaseAmount: string;
    carbonCreditsTransacted: string;
    vintageYear: string;
    serialNumber: string;
    participantRegistryLink: string;
};

type FieldError = {message: string; blocking: boolean};
type Errors = Partial<Record<keyof FormData, FieldError[]>>;

function emptyForm(): FormData {
    return {
        partnerId: null,
        fundedYear: '',
        fundedYearValidated: true,
        fundingSource: '',
        dollarsDonated: '',
        dollarsDonatedValidated: true,
        ttfTrees: '',
        ttfTreesValidated: true,
        communityTreesPlanted: '',
        communityTreesPlantedValidated: true,
        communityTreesDistributed: '',
        communityTreesDistributedValidated: true,
        communityBudgetPassThrough: '',
        communityBudgetPassThroughValidated: true,
        excluded: false,
        showOnDate: '',
        carbonPurchaseAmount: '',
        carbonCreditsTransacted: '',
        vintageYear: '',
        serialNumber: '',
        participantRegistryLink: ''
    };
}

function dataToForm(data: ParticipationData): FormData {
    return {
        partnerId: data.PartnerId,
        fundedYear: data.FundedYear != null ? String(data.FundedYear) : '',
        fundedYearValidated: data.FundedYearValidated ?? false,
        fundingSource: data.FundingSource ?? '',
        dollarsDonated: data.DollarsDonated != null ? String(data.DollarsDonated) : '',
        dollarsDonatedValidated: data.DollarsDonatedValidated ?? false,
        ttfTrees: data.TTFTrees != null ? String(data.TTFTrees) : '',
        ttfTreesValidated: data.TTFTreesValidated ?? false,
        communityTreesPlanted:
            data.CommunityTreesPlanted != null ? String(data.CommunityTreesPlanted) : '',
        communityTreesPlantedValidated: data.CommunityTreesPlantedValidated ?? false,
        communityTreesDistributed:
            data.CommunityTreesDistributed != null ? String(data.CommunityTreesDistributed) : '',
        communityTreesDistributedValidated: data.CommunityTreesDistributedValidated ?? false,
        communityBudgetPassThrough:
            data.CommunityBudgetPassThrough != null ? String(data.CommunityBudgetPassThrough) : '',
        communityBudgetPassThroughValidated: data.CommunityBudgetPassThroughValidated ?? false,
        excluded: data.Excluded,
        showOnDate: data.ShowOnDate ? data.ShowOnDate.substring(0, 10) : '',
        carbonPurchaseAmount:
            data.CarbonPurchaseAmount != null ? String(data.CarbonPurchaseAmount) : '',
        carbonCreditsTransacted:
            data.CarbonCreditsTransacted != null ? String(data.CarbonCreditsTransacted) : '',
        vintageYear: data.VintageYear ?? '',
        serialNumber: data.SerialNumber ?? '',
        participantRegistryLink: data.ParticipantRegistryLink ?? ''
    };
}

function parseNum(v: string): number | null {
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
}

function parseWholeNum(v: string): number | null {
    const n = parseInt(v, 10);
    return isNaN(n) ? null : n;
}

function decimalPlaces(n: number): number {
    if (!isFinite(n)) return 0;
    const s = n.toString();
    const dot = s.indexOf('.');
    return dot < 0 ? 0 : s.length - dot - 1;
}

function validate(
    form: FormData,
    projectType: PlantingProjectType,
    mode: 'add' | 'edit',
    dollarsDonatedLabel: string
): Errors {
    const errors: Errors = {};

    function push(field: keyof FormData, message: string, blocking: boolean) {
        if (!errors[field]) errors[field] = [];
        errors[field].push({message, blocking});
    }

    if (mode === 'add' && !form.partnerId) {
        push('partnerId', 'Partner is required', true);
    }

    if (!form.fundedYear) {
        push('fundedYear', 'Funded year is required to save changes', true);
    }

    const isRef = projectType === PlantingProjectType.Reforestation;
    const isCom = projectType === PlantingProjectType.CommunityEvent;
    const isCTD = projectType === PlantingProjectType.CommunityTreeDistributor;
    const isMem = projectType === PlantingProjectType.MembershipTrees;

    if (isRef) {
        const ttf = parseWholeNum(form.ttfTrees);
        if (ttf === null) {
            // Validated checkbox is shown for Reforestation — if checked, field becomes required to save
            if (form.ttfTreesValidated)
                push('ttfTrees', 'TTF trees is required when marked as validated', true);
            else
                push('ttfTrees', 'Reforestation trees is required to show to the partner', false);
        } else if (ttf < 0) push('ttfTrees', "Reforestation trees can't be negative", true);
        else if (decimalPlaces(ttf) > 0)
            push('ttfTrees', 'Reforestation trees must be a whole number', true);
    }

    if (isCom || isMem) {
        const ctp = parseWholeNum(form.communityTreesPlanted);
        if (ctp === null) {
            // Validated checkbox is only shown for Community — for MembershipTrees keep as warning
            if (form.communityTreesPlantedValidated && isCom)
                push(
                    'communityTreesPlanted',
                    'Community trees planted is required when marked as validated',
                    true
                );
            else
                push(
                    'communityTreesPlanted',
                    'Community trees planted is required to show to the partner',
                    false
                );
        } else if (ctp < 0)
            push('communityTreesPlanted', "Community trees planted can't be negative", true);
        else if (decimalPlaces(ctp) > 0)
            push('communityTreesPlanted', 'Community trees planted must be a whole number', true);
    }

    if (isCom || isCTD) {
        const ctd = parseWholeNum(form.communityTreesDistributed);
        if (ctd === null) {
            // Validated checkbox is only shown for Community — for CTD keep as warning
            if (form.communityTreesDistributedValidated && isCom)
                push(
                    'communityTreesDistributed',
                    'Community trees distributed is required when marked as validated',
                    true
                );
            else
                push(
                    'communityTreesDistributed',
                    'Community trees distributed is required to show to the partner',
                    false
                );
        } else if (ctd < 0)
            push('communityTreesDistributed', "Community trees distributed can't be negative", true);
        else if (decimalPlaces(ctd) > 0)
            push(
                'communityTreesDistributed',
                'Community trees distributed must be a whole number',
                true
            );
    }

    if (isRef || isCom || isCTD) {
        const dollars = parseNum(form.dollarsDonated);
        if (dollars !== null) {
            if (dollars < 0)
                push('dollarsDonated', `${dollarsDonatedLabel} can't be negative`, true);
            else if (decimalPlaces(dollars) > 2)
                push(
                    'dollarsDonated',
                    `${dollarsDonatedLabel} can't have fractions of a cent`,
                    true
                );
        }
    }

    if (isCom) {
        const bpt = parseNum(form.communityBudgetPassThrough);
        if (bpt !== null) {
            if (bpt < 0)
                push('communityBudgetPassThrough', "Budget pass through can't be negative", true);
            else if (decimalPlaces(bpt) > 2)
                push(
                    'communityBudgetPassThrough',
                    "Budget pass through can't have fractions of a cent",
                    true
                );
        }
    }

    return errors;
}

function hasBlockingErrors(errors: Errors): boolean {
    return Object.values(errors).some((arr) => arr?.some((e) => e.blocking));
}

const FormBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const TwoCol = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
`;

const FooterRow = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding-top: 1rem;
    border-top: 1px solid #dee2e6;
`;


const reactSelectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? '#80bdff' : '#ced4da',
        borderRadius: '0.25rem',
        fontSize: '1rem',
        fontWeight: '400',
        lineHeight: '1.5',
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none',
        '&:hover': {borderColor: state.isFocused ? '#80bdff' : '#ced4da'},
        minHeight: '38px'
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0.25rem 0.75rem'
    }),
    input: (base) => ({
        ...base,
        padding: '0',
        margin: '0',
        color: '#212529'
    }),
    placeholder: (base) => ({...base, color: '#6c757d'}),
    singleValue: (base) => ({...base, color: '#212529'}),
    indicatorSeparator: () => ({display: 'none'}),
    dropdownIndicator: (base) => ({...base, padding: '0 0.5rem'}),
    menu: (base) => ({...base, zIndex: 9999})
};

type Props = {
    show: boolean;
    mode: 'add' | 'edit';
    participationId?: number;
    partnerName?: string;
    projectId: number;
    projectType: PlantingProjectType;
    partners: PartnerOption[];
    onSaved: () => void;
    onClose: () => void;
    proposalMode?: boolean;
};

export function ParticipationModal({
    show,
    mode,
    participationId,
    partnerName: partnerNameProp,
    projectId,
    projectType,
    partners,
    onSaved,
    onClose,
    proposalMode
}: Props) {
    const [form, setForm] = useState<FormData>(emptyForm());
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (!show) {
            setSubmitted(false);
            return;
        }
        if (mode === 'add') {
            setForm(emptyForm());
        } else if (mode === 'edit' && participationId != null) {
            setLoading(true);
            fetch(`/api/participations/${participationId}`)
                .then((r) => r.json())
                .then((data: ParticipationData) => {
                    setForm(dataToForm(data));
                })
                .finally(() => setLoading(false));
        }
    }, [show, mode, participationId]);

    function set(field: keyof FormData, value: string | boolean | number | null) {
        setForm((prev) => ({...prev, [field]: value}));
    }

    const dollarsDonatedLabel = proposalMode ? 'Proposed Donation Amount' : 'Dollars donated';
    const errors = validate(form, projectType, mode, dollarsDonatedLabel);

    function renderErrors(field: keyof FormData) {
        if (!submitted || !errors[field]) return null;
        return errors[field].map((e, i) =>
            e.blocking ? (
                <NotValidToSaveValidation key={i}>{e.message}</NotValidToSaveValidation>
            ) : (
                <NotValidToShowToPartnerValidation key={i}>
                    {e.message}
                </NotValidToShowToPartnerValidation>
            )
        );
    }

    function hasError(field: keyof FormData, type: 'blocking' | 'warning'): boolean {
        if (!submitted || !errors[field]) return false;
        return type === 'blocking'
            ? errors[field].some((e) => e.blocking)
            : errors[field].some((e) => !e.blocking);
    }

    function renderValidated(validatedField: keyof FormData, warningField: keyof FormData) {
        // Matches the partner views: can't mark validated while the field's required-to-show
        // validation is unmet
        const disabled = hasError(warningField, 'warning');
        return (
            <ValidateControl
                validated={!!form[validatedField] && !disabled}
                disabled={disabled}
                onChange={(e) => set(validatedField, e.validated)}
            />
        );
    }

    async function handleSave() {
        setSubmitted(true);
        if (hasBlockingErrors(errors)) return;

        setSaving(true);
        try {
            const body = {
                PartnerId: form.partnerId,
                PlantingProjectId: projectId,
                FundedYear: parseWholeNum(form.fundedYear),
                FundedYearValidated: form.fundedYearValidated,
                FundingSource: form.fundingSource || null,
                DollarsDonated: parseNum(form.dollarsDonated),
                DollarsDonatedValidated: form.dollarsDonatedValidated,
                TTFTrees: parseWholeNum(form.ttfTrees),
                TTFTreesValidated: form.ttfTreesValidated,
                CommunityTreesPlanted: parseWholeNum(form.communityTreesPlanted),
                CommunityTreesPlantedValidated: form.communityTreesPlantedValidated,
                CommunityTreesDistributed: parseWholeNum(form.communityTreesDistributed),
                CommunityTreesDistributedValidated: form.communityTreesDistributedValidated,
                CommunityBudgetPassThrough: parseNum(form.communityBudgetPassThrough),
                CommunityBudgetPassThroughValidated: form.communityBudgetPassThroughValidated,
                Excluded: form.excluded,
                ShowOnDate: form.showOnDate || null,
                CarbonPurchaseAmount: parseNum(form.carbonPurchaseAmount),
                CarbonCreditsTransacted: parseWholeNum(form.carbonCreditsTransacted),
                VintageYear: form.vintageYear || null,
                SerialNumber: form.serialNumber || null,
                ParticipantRegistryLink: form.participantRegistryLink || null,
                ProjectType: projectType
            };

            if (mode === 'add') {
                await fetch('/api/participations', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(body)
                });
            } else {
                await fetch(`/api/participations/${participationId}`, {
                    method: 'PUT',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(body)
                });
            }
            onSaved();
            onClose();
        } finally {
            setSaving(false);
        }
    }

    const isRef = projectType === PlantingProjectType.Reforestation;
    const isCom = projectType === PlantingProjectType.CommunityEvent;
    const isCarbon = projectType === PlantingProjectType.Carbon;
    const isCTD = projectType === PlantingProjectType.CommunityTreeDistributor;
    const isMem = projectType === PlantingProjectType.MembershipTrees;

    const showFundingSource = isRef || isCom || isCTD || isCarbon;
    const showDollars = isRef || isCom || isCTD;
    const showTTFTrees = isRef;
    const showCommunityPlanted = isCom || isMem;
    const showCommunityDistributed = isCom || isCTD;
    const showBudgetPassThrough = isCom;
    const showShowOnDate = isRef || isCom || isMem;

    // Validation checkboxes only exist for Reforestation and Community, matching the partner views
    const showFundedYearValidated = isRef || isCom;
    const showDollarsValidated = isRef || isCom;
    const showTTFTreesValidated = isRef;
    const showCommunityPlantedValidated = isCom;
    const showCommunityDistributedValidated = isCom;
    const showBudgetValidated = isCom;

    const partnerOptions = partners.map((p) => ({value: p.Id, label: p.Name}));
    const selectedPartner = partnerOptions.find((p) => p.value === form.partnerId) ?? null;

    return (
        <Modal
            showModal={show}
            onClose={onClose}
            title={<strong>{mode === 'add' ? 'Add Participation' : 'Edit Participation'}</strong>}
            width="620px"
        >
            {loading ? (
                <div className="text-center py-3">Loading...</div>
            ) : (
                <FormBody>
                    {mode === 'add' ? (
                        <FormFieldContainer>
                            <LabelContainer>
                                <Label>Partner</Label>
                                <RequiredSignifier />
                            </LabelContainer>
                            <ReactSelect
                                options={partnerOptions}
                                value={selectedPartner}
                                onChange={(opt) => set('partnerId', opt?.value ?? null)}
                                placeholder="Search partners..."
                                isClearable
                                styles={reactSelectStyles}
                            />
                            {renderErrors('partnerId')}
                        </FormFieldContainer>
                    ) : (
                        <div>
                            <Label>Partner: <strong>{partnerNameProp}</strong></Label>
                        </div>
                    )}

                    <TwoCol>
                        <FormFieldContainer>
                            <LabelContainer>
                                <Label>{isCarbon ? 'Purchase year' : 'Funded year'}</Label>
                                <RequiredSignifier />
                                {showFundedYearValidated &&
                                    renderValidated('fundedYearValidated', 'fundedYear')}
                            </LabelContainer>
                            <NumberControl
                                value={form.fundedYear}
                                onChange={(e) => set('fundedYear', e.target.value)}
                                notValidToSave={hasError('fundedYear', 'blocking')}
                                placeholder="e.g. 2024"
                            />
                            {renderErrors('fundedYear')}
                        </FormFieldContainer>

                        {showFundingSource && (
                            <FormFieldContainer>
                                <Label>Funding source</Label>
                                <TextControl
                                    maxLength={75}
                                    value={form.fundingSource}
                                    onChange={(e) => set('fundingSource', e.target.value)}
                                />
                            </FormFieldContainer>
                        )}
                    </TwoCol>

                    {!isCarbon && (
                        <TwoCol>
                            {showTTFTrees && (
                                <FormFieldContainer>
                                    <LabelContainer>
                                        <Label>TTF trees</Label>
                                        <RequiredToShowToPartnerSignifier />
                                        {showTTFTreesValidated &&
                                            renderValidated('ttfTreesValidated', 'ttfTrees')}
                                    </LabelContainer>
                                    <NumberControl
                                        value={form.ttfTrees}
                                        onChange={(e) => set('ttfTrees', e.target.value)}
                                        notValidToSave={hasError('ttfTrees', 'blocking')}
                                        notValidToShowPartner={hasError('ttfTrees', 'warning')}
                                    />
                                    {renderErrors('ttfTrees')}
                                </FormFieldContainer>
                            )}
                            {showCommunityPlanted && (
                                <FormFieldContainer>
                                    <LabelContainer>
                                        <Label>Community trees planted</Label>
                                        <RequiredToShowToPartnerSignifier />
                                        {showCommunityPlantedValidated &&
                                            renderValidated(
                                                'communityTreesPlantedValidated',
                                                'communityTreesPlanted'
                                            )}
                                    </LabelContainer>
                                    <NumberControl
                                        value={form.communityTreesPlanted}
                                        onChange={(e) =>
                                            set('communityTreesPlanted', e.target.value)
                                        }
                                        notValidToSave={hasError('communityTreesPlanted', 'blocking')}
                                        notValidToShowPartner={hasError(
                                            'communityTreesPlanted',
                                            'warning'
                                        )}
                                    />
                                    {renderErrors('communityTreesPlanted')}
                                </FormFieldContainer>
                            )}
                            {showCommunityDistributed && (
                                <FormFieldContainer>
                                    <LabelContainer>
                                        <Label>Community trees distributed</Label>
                                        <RequiredToShowToPartnerSignifier />
                                        {showCommunityDistributedValidated &&
                                            renderValidated(
                                                'communityTreesDistributedValidated',
                                                'communityTreesDistributed'
                                            )}
                                    </LabelContainer>
                                    <NumberControl
                                        value={form.communityTreesDistributed}
                                        onChange={(e) =>
                                            set('communityTreesDistributed', e.target.value)
                                        }
                                        notValidToSave={hasError(
                                            'communityTreesDistributed',
                                            'blocking'
                                        )}
                                        notValidToShowPartner={hasError(
                                            'communityTreesDistributed',
                                            'warning'
                                        )}
                                    />
                                    {renderErrors('communityTreesDistributed')}
                                </FormFieldContainer>
                            )}
                            {showDollars && (
                                <FormFieldContainer>
                                    <LabelContainer>
                                        <Label>{dollarsDonatedLabel}</Label>
                                        {showDollarsValidated &&
                                            renderValidated(
                                                'dollarsDonatedValidated',
                                                'dollarsDonated'
                                            )}
                                    </LabelContainer>
                                    <NumberControl
                                        step="0.01"
                                        value={form.dollarsDonated}
                                        onChange={(e) => set('dollarsDonated', e.target.value)}
                                        notValidToSave={hasError('dollarsDonated', 'blocking')}
                                    />
                                    {renderErrors('dollarsDonated')}
                                </FormFieldContainer>
                            )}
                            {showBudgetPassThrough && (
                                <FormFieldContainer>
                                    <LabelContainer>
                                        <Label>Community budget pass through</Label>
                                        {showBudgetValidated &&
                                            renderValidated(
                                                'communityBudgetPassThroughValidated',
                                                'communityBudgetPassThrough'
                                            )}
                                    </LabelContainer>
                                    <NumberControl
                                        step="0.01"
                                        value={form.communityBudgetPassThrough}
                                        onChange={(e) =>
                                            set('communityBudgetPassThrough', e.target.value)
                                        }
                                        notValidToSave={hasError(
                                            'communityBudgetPassThrough',
                                            'blocking'
                                        )}
                                    />
                                    {renderErrors('communityBudgetPassThrough')}
                                </FormFieldContainer>
                            )}
                            {showShowOnDate && (
                                <FormFieldContainer>
                                    <Label>Show on date</Label>
                                    <DateControl
                                        value={form.showOnDate}
                                        onChange={(e) => set('showOnDate', e.target.value)}
                                    />
                                </FormFieldContainer>
                            )}
                        </TwoCol>
                    )}

                    {isCarbon && (
                        <TwoCol>
                            <FormFieldContainer>
                                <Label>Verified Carbon Credits</Label>
                                <NumberControl
                                    value={form.carbonCreditsTransacted}
                                    onChange={(e) => set('carbonCreditsTransacted', e.target.value)}
                                />
                            </FormFieldContainer>
                            <FormFieldContainer>
                                <Label>Carbon purchase amount</Label>
                                <NumberControl
                                    step="0.01"
                                    value={form.carbonPurchaseAmount}
                                    onChange={(e) => set('carbonPurchaseAmount', e.target.value)}
                                />
                            </FormFieldContainer>
                            <FormFieldContainer>
                                <Label>Vintage year</Label>
                                <TextControl
                                    value={form.vintageYear}
                                    onChange={(e) => set('vintageYear', e.target.value)}
                                />
                            </FormFieldContainer>
                            <FormFieldContainer>
                                <Label>Serial number</Label>
                                <TextControl
                                    value={form.serialNumber}
                                    onChange={(e) => set('serialNumber', e.target.value)}
                                />
                            </FormFieldContainer>
                            <FormFieldContainer style={{gridColumn: '1 / -1'}}>
                                <Label>Participant registry link</Label>
                                <TextControl
                                    value={form.participantRegistryLink}
                                    onChange={(e) =>
                                        set('participantRegistryLink', e.target.value)
                                    }
                                />
                            </FormFieldContainer>
                        </TwoCol>
                    )}

                    <FormFieldContainer>
                        <CheckboxLabel>
                            <input
                                type="checkbox"
                                checked={form.excluded}
                                onChange={(e) => set('excluded', e.target.checked)}
                            />
                            Excluded
                        </CheckboxLabel>
                    </FormFieldContainer>

                    <FooterRow>
                        <CancelButton onClick={onClose} disabled={saving}>
                            Cancel
                        </CancelButton>
                        <SaveChangesButton
                            onClick={handleSave}
                            disabled={saving || hasBlockingErrors(errors)}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </SaveChangesButton>
                    </FooterRow>
                </FormBody>
            )}
        </Modal>
    );
}
